import { useEffect, useState } from 'react'
import { usePlaidLink } from 'react-plaid-link'
import { useNavigate } from 'react-router-dom'
import { useDisclosure } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import { useIsMutating, useQueryClient } from '@tanstack/react-query'

import { AccountsRoutePathsEnum } from '@/modules/accounts/routes/accountsRouteConfig.types'

import { ADD_ACCOUNT_ITEMS } from '@/components/AddAccount/AddAccount.config'
import { ButtonMenu } from '@/components/ButtonMenu/ButtonMenu'
import { ManualAccountAdd } from '@/components/ManualAccountAdd/ManualAccountAdd'
import { ManualAccountModal } from '@/components/ManualAccountModal/ManualAccountModal'
import { ResponsiveOverlay } from '@/components/ResponsiveOverlay/ResponsiveOverlay'

import { INVALIDATE_KEYS } from '@/constants/invalidateAccountQueryKeys.constants'
import { TRANSACTION_NOTICE_KEY } from '@/constants/localStorageKeys.constants'

import {
  useBankingServiceBankingControllerCreateAccounts,
  useBankingServiceBankingControllerCreateLinkToken,
} from '@/__generated__/queries'
import type { CreateLinkTokenDTO } from '@/__generated__/requests'

export function AddAccount() {
  const [plaidToken, setPlaidToken] = useState<string | null>(null)
  const [isPlaidLoading, setIsPlaidLoading] = useState(false)
  const [isOverlayVisible, setIsOverlayVisible] = useState(false)

  const [opened, { close, open: openManualAccountForm }] = useDisclosure(false)

  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const isMutating = useIsMutating({
    exact: true,
  })

  const createTokenMutation = useBankingServiceBankingControllerCreateLinkToken(
    {
      onSuccess: data => {
        setPlaidToken(data.token)
        setIsPlaidLoading(true)
      },
    },
  )

  const createAccountMutation =
    useBankingServiceBankingControllerCreateAccounts({
      onSuccess: data => {
        if (data.skippedAccounts > 0) {
          notifications.show({
            color: 'red',
            message:
              'One or more account could not be added. Please check if you have already added the account',
            title: 'Error',
          })

          return
        }

        notifications.show({
          color: 'green',
          message: 'Account added successfully',
          title: 'Success',
        })

        const transactionsNoticeExpire = Date.now() + 1 * 60 * 1000

        localStorage.setItem(
          TRANSACTION_NOTICE_KEY,
          transactionsNoticeExpire.toString(),
        )

        navigate(AccountsRoutePathsEnum.ACCOUNTS)

        INVALIDATE_KEYS.forEach(key => {
          queryClient.invalidateQueries({
            queryKey: [key],
          })
        })
      },
    })

  const { open, ready } = usePlaidLink({
    onEvent: eventName => {
      if (eventName === 'EXIT' || eventName === 'HANDOFF')
        setIsOverlayVisible(false)
    },
    onLoad: () => {
      setIsPlaidLoading(false)
      setIsOverlayVisible(true)
    },
    onSuccess: (public_token, metadata) => {
      const accounts = metadata?.accounts || []

      createAccountMutation.mutate({
        requestBody: {
          accessToken: public_token,
          accounts: accounts.map(({ id, mask, name, subtype, type }) => ({
            id,
            mask,
            name,
            subtype,
            type,
          })),
          institutionId: metadata?.institution?.institution_id || '',
          institutionName: metadata?.institution?.name || '',
        },
      })
    },
    token: plaidToken,
  })

  useEffect(() => {
    if (plaidToken && ready) {
      open()
    }
  }, [plaidToken, ready, open])

  const handleAddAccount = (param?: string) => {
    if (!param) {
      return
    }

    if (param === 'manual') {
      openManualAccountForm()

      return
    }

    const params = param.split(', ')

    createTokenMutation.mutate({
      requestBody: {
        products:
          param[0] !== 'all'
            ? ([params[0]] as unknown as CreateLinkTokenDTO['products'])
            : [],
      },
    })
  }

  return (
    <>
      <ButtonMenu
        items={ADD_ACCOUNT_ITEMS}
        label="Add account"
        loading={isPlaidLoading || !!isMutating}
        onItemSelect={handleAddAccount}
      />

      <ManualAccountModal onClose={close} opened={opened}>
        <ManualAccountAdd onClose={close} />
      </ManualAccountModal>

      <ResponsiveOverlay isVisible={isOverlayVisible} visibleOnMobile={false} />
    </>
  )
}
